import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(date));
}

/**
 * Format a calendar-day field (no meaningful time-of-day) using its UTC date,
 * so the day doesn't shift backwards for viewers in negative-offset timezones.
 * Use for dates that represent a day, not an instant (e.g. requestedShipDate,
 * which the importer stores at noon-UTC but older rows hold at midnight-UTC).
 * For true timestamps (createdAt, updatedAt, …) use formatDate/formatDateTime.
 */
export function formatDateUTC(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(date));
}

export function formatDateTime(date: Date | string): string {
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(date));
}

/** Compact display for opaque ids: first 8 chars + ellipsis (or an em dash). */
export function truncateId(id: string | null | undefined): string {
  if (!id) return '—';
  return id.length > 12 ? `${id.slice(0, 8)}…` : id;
}

// Dev Features
export function isDevFeatures(): boolean {
  return import.meta.env.VITE_DEV_FEATURES === 'true';
}

export function withDownload(url: string): string {
  const u = new URL(url, window.location.origin);
  u.searchParams.set('download', 'true');
  return u.toString();
}
